import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import { assets } from '../editor/storage';
import {
  fileOf, FLUENT_STYLES, freeItems, groupItem, ICON_INK, kindOf, PAGE_TILES, PHOSPHOR_STYLES, placeScale, rasterSize, searchItems,
  tintSvg, vectorPlan, type FreeSearch, type GfxItem
} from '../graphics/graphics';
import { api, type FreeSetInfo, type GraphicCategory, type GraphicInfo } from '../sync/api';
import { $, $in } from './dom';
import { closeSheets, whenOpened } from './sheets';
import { toast } from './toast';

/*
 * องค์ประกอบ → กราฟิก (0019): search the group's library and the free sets (Thai or English words), pick a style,
 * tap a tile to put it on the page.
 *   vector (SVG)   becomes shapes in the work: recolour it in the toolbar ("สี"), sharp at every size and in print PDFs
 *   picture        (3D emoji, PNG/JPG/WebP of the library, an SVG with effects Fabric cannot draw) becomes the person's
 *                  own picture, saved with the work like an upload — so a graphic hidden or deleted later never breaks it
 *   group library  members with the right 'graphics' (and admins) add; whoever added one (or an admin) edits/hides/deletes
 *   free sets      shown once an admin has imported them (หลังบ้าน → ตั้งค่า → คลังกราฟิก ชุดฟรี)
 */
export interface GraphicsHooks {
  canEdit: () => boolean;
  /** may add graphics (admin, or the right 'graphics') */
  canAdd: () => boolean;
  isAdmin: () => boolean;
  /** the shapes (src/ui/shapePanel.ts): shown in แท็บ "ทั้งหมด" when searching, put in as shapes */
  shapes?: { items: () => GfxItem[]; insert: (id: string) => void };
}

type Tab = 'all' | 'group' | 'phosphor' | 'fluent';
const TABS: Array<{ id: Tab; th: string }> = [
  { id: 'all', th: 'ทั้งหมด' }, { id: 'group', th: 'ของกลุ่ม' }, { id: 'phosphor', th: 'ไอคอน' }, { id: 'fluent', th: 'อีโมจิ' }
];
const SVG_TYPE = 'image/svg+xml';
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

/** a file of the library or a free set; a Thai reason when it cannot be had */
async function fetchFile(url: string): Promise<Response> {
  let r: Response;
  try { r = await fetch(url, { credentials: 'same-origin' }); }
  catch { throw new Error('โหลดไฟล์กราฟิกไม่ได้ (ไม่มีอินเทอร์เน็ตหรือเซิร์ฟเวอร์ไม่ตอบ)'); }
  if (!r.ok) {
    const body = (r.headers.get('content-type') ?? '').includes('json') ? await r.json().catch(() => ({})) as { error?: string } : {};
    throw new Error(body.error || `โหลดไฟล์กราฟิกไม่สำเร็จ (${r.status})`);
  }
  return r;
}

/** the size an SVG says it is (viewBox, else width/height) */
function svgSize(svg: string): { w: number; h: number } {
  const doc = new DOMParser().parseFromString(svg, SVG_TYPE).documentElement;
  const vb = (doc.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) return { w: vb[2], h: vb[3] };
  return { w: parseFloat(doc.getAttribute('width') ?? '') || 0, h: parseFloat(doc.getAttribute('height') ?? '') || 0 };
}

/** an SVG drawn into a PNG (RASTER_SIDE on its longer side) — for what Fabric cannot draw as shapes */
async function svgToPng(svg: string): Promise<Blob> {
  const size = rasterSize(svgSize(svg).w, svgSize(svg).h);
  const doc = new DOMParser().parseFromString(svg, SVG_TYPE);
  const root = doc.documentElement;
  if (root.querySelector('parsererror') || root.nodeName.toLowerCase() !== 'svg') throw new Error('ไฟล์ SVG เสีย อ่านไม่ได้');
  root.setAttribute('width', String(size.w)); root.setAttribute('height', String(size.h));
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(doc)], { type: SVG_TYPE }));
  try {
    const img = new Image();
    await new Promise<void>((ok, bad) => { img.onload = () => ok(); img.onerror = () => bad(new Error('เบราว์เซอร์วาด SVG นี้ไม่ได้')); img.src = url; });
    const c = document.createElement('canvas');
    c.width = size.w; c.height = size.h;
    c.getContext('2d')!.drawImage(img, 0, 0, size.w, size.h);
    const png = await new Promise<Blob | null>(ok => c.toBlob(ok, 'image/png'));
    c.width = c.height = 0;
    if (!png) throw new Error('แปลงกราฟิกเป็นภาพไม่สำเร็จ (หน่วยความจำเครื่องอาจไม่พอ)');
    return png;
  } finally { URL.revokeObjectURL(url); }
}

export function initGraphicsPanel(ed: Editor, h: GraphicsHooks) {
  const grid = $('gGrid'), msg = $('gMsg'), more = $<HTMLButtonElement>('gMore'), count = $('gCount'), search = $in('gSearch');
  let cats: GraphicCategory[] = [];
  let lib: GraphicInfo[] = [];
  let free: FreeSetInfo[] = [];
  let sets: { fluent: GfxItem[]; phosphor: GfxItem[] } | null = null;
  let tab: Tab = 'all', cat = '', shown = PAGE_TILES;
  let fluentStyle: string = '3d', phosphorStyle: string = 'regular';
  let loading: Promise<void> | null = null, loadError = '';
  let busy = false;
  let editing: string | null = null;   // the library graphic whose edit box is open
  let armedDelete: string | null = null;

  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };
  const ready = (set: 'fluent' | 'phosphor') => !!free.find(f => f.set === set)?.ready;
  const styleOf = (it: GfxItem) => it.src === 'fluent' ? fluentStyle : it.src === 'phosphor' ? phosphorStyle : '';
  const canManage = (it: GfxItem) => it.src === 'group' && (h.isAdmin() || (!!it.mine && h.canAdd()));

  async function load() {
    loadError = '';
    try {
      const [list, search] = await Promise.all([
        api.graphics(),
        // the free sets' search words (≈ 500 KB) are loaded only when the panel is first used
        sets ? Promise.resolve(null) : import('../graphics/freeSearch.json').then(m => m.default as unknown as FreeSearch)
      ]);
      cats = list.categories; lib = list.graphics; free = list.free;
      if (search) sets = freeItems(search);
    } catch (e) {
      loadError = `โหลดคลังกราฟิกไม่สำเร็จ: ${errText(e)}`;
    }
    paint();
  }
  const reload = () => { loading = load(); return loading; };

  /** what the current tab, chips and search show */
  function list(): GfxItem[] {
    const group = lib.map(groupItem).filter(it => !cat || it.group === cat);
    const icons = sets && ready('phosphor') ? sets.phosphor : [];
    const emoji = sets && ready('fluent') ? sets.fluent : [];
    // a search in "ทั้งหมด" finds shapes too ("หัวใจ" → รูปทรงหัวใจ), first
    const shapes = tab === 'all' && !cat && search.value.trim() && h.shapes ? h.shapes.items() : [];
    const pool = tab === 'group' ? group : tab === 'phosphor' ? icons : tab === 'fluent' ? emoji : [...shapes, ...group, ...emoji, ...icons];
    return searchItems(pool, search.value);
  }

  function chipRow(box: HTMLElement, items: Array<{ id: string; th: string }>, on: string, pick: (id: string) => void) {
    box.textContent = '';
    for (const c of items) {
      const b = el('button', 'fchip', c.th);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(c.id === on));
      b.onclick = () => { pick(c.id); shown = PAGE_TILES; paint(); };
      box.append(b);
    }
    box.hidden = !items.length;
  }

  function paint() {
    chipRow($('gTabs'), TABS, tab, id => { tab = id as Tab; cat = ''; editing = null; });
    // the style of the set shown (all: both rows)
    const styles = $('gStyles');
    styles.textContent = '';
    if ((tab === 'fluent' || tab === 'all') && ready('fluent')) {
      const row = el('div', 'fchips');
      chipRow(row, FLUENT_STYLES.map(s => ({ id: s.id, th: `อีโมจิ${s.th}` })), fluentStyle, id => { fluentStyle = id; });
      styles.append(row);
    }
    if ((tab === 'phosphor' || tab === 'all') && ready('phosphor')) {
      const row = el('div', 'fchips');
      chipRow(row, PHOSPHOR_STYLES.map(s => ({ id: s.id, th: `ไอคอน${s.th}` })), phosphorStyle, id => { phosphorStyle = id; });
      styles.append(row);
    }
    chipRow($('gCats'), tab === 'group' ? [{ id: '', th: `ทุกหมวด ${lib.length}` }, ...cats.map(c => ({ id: c.id, th: `${c.name} ${lib.filter(g => g.categoryId === c.id).length}` }))] : [], cat, id => { cat = id; });

    // what is not there yet, and why
    const notes: string[] = [];
    if (loadError) notes.push(loadError);
    for (const f of free) if (!f.ready && (tab === 'all' || tab === f.set)) {
      notes.push(`${f.set === 'fluent' ? 'อีโมจิ' : 'ไอคอน'}ชุดฟรี (${f.name}) ยังไม่พร้อม — นำเข้าแล้ว ${Math.floor(f.done / f.chunks * 100)}% `
        + (h.isAdmin() ? 'กด "นำเข้าชุดฟรี" ที่ หลังบ้านผู้ดูแล → ตั้งค่า' : 'ผู้ดูแลต้องกด "นำเข้าชุดฟรี" ที่หลังบ้านก่อน'));
    }
    say(notes.join('\n'));

    const all = list();
    grid.textContent = '';
    if (!all.length) {
      grid.append(el('p', 'empty', loading && !lib.length && !sets ? 'กำลังโหลด…'
        : search.value.trim() ? `ไม่พบ "${search.value.trim()}" ลองคำอื่น เช่น หัวใจ ดาว ลูกศร บ้าน`
        : tab === 'group' ? (h.canAdd() ? 'ยังไม่มีกราฟิกของกลุ่ม เพิ่มได้ด้านล่าง' : 'ยังไม่มีกราฟิกของกลุ่ม ผู้ดูแลหรือคนที่ได้สิทธิ์เป็นคนเพิ่ม') : 'ยังไม่มีกราฟิกให้เลือก'));
    }
    for (const it of all.slice(0, shown)) grid.append(tile(it));
    more.hidden = all.length <= shown;
    more.textContent = `แสดงเพิ่ม (เหลืออีก ${(all.length - shown).toLocaleString('th-TH')})`;
    count.textContent = all.length ? `แสดง ${Math.min(shown, all.length).toLocaleString('th-TH')} จาก ${all.length.toLocaleString('th-TH')} ชิ้น` : '';
    $('gAddOpen').hidden = !h.canAdd();
    // admins: add a category; delete the chosen one when it is empty (the server says why otherwise)
    $('gCatAdmin').hidden = !(h.isAdmin() && tab === 'group');
    $('gCatDel').hidden = !cat;
    paintEdit();
  }

  function tile(it: GfxItem): HTMLElement {
    const wrap = el('div', 'gtile' + (it.hidden ? ' ghidden' : ''));
    const b = el('button', 'gpick');
    b.type = 'button';
    b.title = it.title;
    b.setAttribute('aria-label', `${it.title}${it.hidden ? ' (ซ่อนอยู่)' : ''}`);
    const img = el('img');
    img.loading = 'lazy'; img.decoding = 'async'; img.alt = '';
    img.src = fileOf(it, styleOf(it));
    img.onerror = () => { b.classList.add('gbroken'); b.title = `${it.title} — โหลดรูปตัวอย่างไม่ได้`; };
    b.append(img);
    b.onclick = () => void insert(it);
    wrap.append(b);
    if (it.hidden) wrap.append(el('small', 'gbadge', 'ซ่อน'));
    if (canManage(it)) {
      const m = el('button', 'gmenu', '⋯');
      m.type = 'button';
      m.setAttribute('aria-label', `แก้ ${it.title}`);
      m.onclick = () => { editing = editing === it.id ? null : it.id; armedDelete = null; paintEdit(); };
      wrap.append(m);
    }
    return wrap;
  }

  /* ---------- put a graphic on the page ---------- */
  async function insert(it: GfxItem) {
    if (busy) return;
    if (it.src === 'shape') { h.shapes?.insert(it.id); return; }
    if (!h.canEdit()) { toast('งานนี้เปิดดูอย่างเดียว ใส่กราฟิกไม่ได้', true); return; }
    if (it.hidden) { toast('กราฟิกนี้ซ่อนอยู่ (ไม่ให้เลือกใช้แล้ว) กด ⋯ เพื่อแสดงอีกครั้งก่อน', true); return; }
    busy = true; grid.classList.add('busy');
    const style = styleOf(it), url = fileOf(it, style);
    const gfx = { src: it.src, id: it.id, style };
    try {
      if (kindOf(it, style) === 'picture') {
        await putPicture(await (await fetchFile(url)).blob(), it, gfx);
      } else {
        let svg = await (await fetchFile(url)).text();
        if (it.src === 'phosphor') svg = tintSvg(svg, ICON_INK);
        const plan = vectorPlan(svg);
        if (plan.vector && await putVector(plan.svg, it, gfx)) { /* done */ }
        else {
          await putPicture(await svgToPng(svg), it, gfx);
          toast(`ใส่เป็นภาพ ${rasterSize(svgSize(svg).w, svgSize(svg).h).w} px แทนเส้น (${plan.vector ? 'แปลงเป็นเส้นไม่ได้' : plan.why}) — เปลี่ยนสีไม่ได้`);
        }
      }
      if (!matchMedia('(min-width: 900px)').matches) closeSheets();
    } catch (e) {
      toast(`ใส่ "${it.title}" ไม่สำเร็จ: ${errText(e)}`, true);
    } finally {
      busy = false; grid.classList.remove('busy');
    }
  }

  /** shapes from an SVG; false when Fabric found nothing it can draw */
  async function putVector(svg: string, it: GfxItem, gfx: object): Promise<boolean> {
    const { objects, options } = await new Promise<{ objects: fabric.Object[]; options: Record<string, unknown> }>(ok =>
      fabric.loadSVGFromString(svg, (objects, options) => ok({ objects: objects.filter(Boolean), options })));
    if (!objects.length) return false;
    // an SVG of one shape comes back as that shape, not a group (Phosphor's regular icons are one path): it is put in a
    // group of its own, so every graphic is a group — the colour boxes, the label and print go by that (BUG-68)
    const made = fabric.util.groupSVGElements(objects, options) as fabric.Object;
    const o = made.type === 'group' ? made : new fabric.Group([made]);
    const s = placeScale(o.width ?? 0, o.height ?? 0, ed.doc.w, ed.doc.h);
    o.set({ scaleX: s, scaleY: s, name: it.title.split(' · ')[0].slice(0, 28) || 'กราฟิก' });
    o.set('gfx' as keyof fabric.Object, gfx);
    ed.place(o);
    return true;
  }

  /** a picture, saved with the work like an upload (src/editor/storage.ts assets → the saver uploads it) */
  async function putPicture(blob: Blob, it: GfxItem, gfx: object) {
    const url = URL.createObjectURL(blob);
    const img = await new Promise<fabric.Image | null>(ok => fabric.Image.fromURL(url, i => ok(i && i.width ? i : null)));
    if (!img) { URL.revokeObjectURL(url); throw new Error('เปิดไฟล์ภาพไม่ได้ (ไฟล์อาจเสีย)'); }
    assets.set(url, blob);
    img.scale(placeScale(img.width ?? 0, img.height ?? 0, ed.doc.w, ed.doc.h));
    img.set({ name: it.title.split(' · ')[0].slice(0, 28) || 'กราฟิก' });
    img.set('gfx' as keyof fabric.Object, gfx);
    ed.place(img);
  }

  /* ---------- add to the group's library ---------- */
  const fileIn = $in('gAddFile');
  let picked: File | null = null;
  $('gAddOpen').onclick = () => { $('gAdd').hidden = !$('gAdd').hidden; paintCats(); };
  $('gAddPick').onclick = () => fileIn.click();
  fileIn.onchange = () => {
    picked = fileIn.files?.[0] ?? null;
    fileIn.value = '';
    $('gAddFileName').textContent = picked ? `${picked.name} · ${(picked.size / 1024).toFixed(0)} KB` : 'ยังไม่ได้เลือกไฟล์';
    if (picked && !$in('gAddName').value.trim()) $in('gAddName').value = picked.name.replace(/\.[^.]+$/, '').slice(0, 80);
  };
  function paintCats() {
    for (const sel of [$<HTMLSelectElement>('gAddCat'), $<HTMLSelectElement>('gEditCat')]) {
      const keep = sel.value;
      sel.textContent = '';
      for (const c of cats) sel.append(new Option(c.name, c.id));
      if (cats.some(c => c.id === keep)) sel.value = keep;
    }
  }
  $('gAddSave').onclick = async () => {
    const out = $('gAddMsg');
    const tell = (t: string, ok = false) => { out.textContent = t; out.hidden = !t; out.classList.toggle('ok', ok); };
    if (!picked) { tell('ยังไม่ได้เลือกไฟล์ (SVG, PNG, JPG หรือ WebP)'); return; }
    if (!$in('gAddRights').checked) { tell('ต้องติ๊กยืนยันว่ากลุ่มมีสิทธิ์ใช้กราฟิกนี้ก่อน'); return; }
    const btn = $<HTMLButtonElement>('gAddSave');
    btn.disabled = true; tell('กำลังเพิ่ม…', true);
    try {
      const r = await api.addGraphic(picked, { name: $in('gAddName').value, keywords: $in('gAddWords').value, categoryId: $<HTMLSelectElement>('gAddCat').value });
      tell(`เพิ่ม "${r.graphic.name}" เข้าคลังแล้ว`, true);
      picked = null; $in('gAddName').value = ''; $in('gAddWords').value = ''; $in('gAddRights').checked = false;
      $('gAddFileName').textContent = 'ยังไม่ได้เลือกไฟล์';
      tab = 'group'; cat = ''; search.value = '';
      await reload();
    } catch (e) { tell(`เพิ่มไม่สำเร็จ: ${errText(e)}`); }
    finally { btn.disabled = false; }
  };

  /* ---------- edit / hide / delete a library graphic (admins, or whoever added it) ---------- */
  function paintEdit() {
    const box = $('gEdit'), g = lib.find(x => x.id === editing);
    box.hidden = !g;
    if (!g) return;
    paintCats();
    $('gEditTitle').textContent = `แก้ "${g.name}" · เพิ่มโดย ${g.createdByName || '—'}`;
    // only filled when another graphic is opened: what is being typed is never wiped by a repaint
    if (box.dataset.id !== g.id) {
      box.dataset.id = g.id;
      $in('gEditName').value = g.name; $in('gEditWords').value = g.keywords;
      $<HTMLSelectElement>('gEditCat').value = g.categoryId;
      $('gEditMsg').hidden = true;
    }
    $('gEditHide').textContent = g.hidden ? 'แสดงอีกครั้ง' : 'ซ่อน (ไม่ให้เลือกใช้)';
    $('gEditDel').textContent = armedDelete === g.id ? 'แตะอีกครั้งเพื่อลบจริง' : 'ลบออกจากคลัง';
  }
  async function editAct(what: string, run: () => Promise<unknown>, done: string) {
    const out = $('gEditMsg');
    out.hidden = false; out.classList.add('ok'); out.textContent = `กำลัง${what}…`;
    try {
      await run();
      out.textContent = done; toast(done);
      await reload();
    } catch (e) { out.classList.remove('ok'); out.textContent = `${what}ไม่สำเร็จ: ${errText(e)}`; await reload(); }
  }
  $('gEditSave').onclick = () => { const id = editing; if (id) void editAct('บันทึก', () => api.patchGraphic(id, { name: $in('gEditName').value, keywords: $in('gEditWords').value, categoryId: $<HTMLSelectElement>('gEditCat').value }), 'บันทึกแล้ว'); };
  $('gEditHide').onclick = () => {
    const g = lib.find(x => x.id === editing); if (!g) return;
    void editAct(g.hidden ? 'แสดง' : 'ซ่อน', () => api.patchGraphic(g.id, { hidden: !g.hidden }), g.hidden ? 'แสดงแล้ว เลือกใช้ได้อีกครั้ง' : 'ซ่อนแล้ว งานที่ใช้ไปแล้วไม่เปลี่ยน');
  };
  $('gEditDel').onclick = () => {
    const g = lib.find(x => x.id === editing); if (!g) return;
    if (armedDelete !== g.id) { armedDelete = g.id; paintEdit(); setTimeout(() => { if (armedDelete === g.id) { armedDelete = null; paintEdit(); } }, 4000); return; }
    armedDelete = null; editing = null;
    void editAct('ลบ', () => api.deleteGraphic(g.id), `ลบ "${g.name}" แล้ว งานที่ใช้ไปแล้วยังมีชิ้นนี้อยู่ (เก็บไว้ในงาน)`);
  };
  $('gEditClose').onclick = () => { editing = null; paintEdit(); };

  /* ---------- categories (admins) ---------- */
  $('gCatAdd').onclick = async () => {
    const name = $in('gCatName').value.trim();
    if (!name) { toast('พิมพ์ชื่อหมวดก่อน', true); return; }
    try { await api.addGraphicCategory(name); $in('gCatName').value = ''; toast(`เพิ่มหมวด "${name}" แล้ว`); await reload(); }
    catch (e) { toast(`เพิ่มหมวดไม่สำเร็จ: ${errText(e)}`, true); }
  };
  $('gCatDel').onclick = async () => {
    const c = cats.find(x => x.id === cat); if (!c) return;
    try { await api.deleteGraphicCategory(c.id); cat = ''; toast(`ลบหมวด "${c.name}" แล้ว`); }
    catch (e) { toast(`ลบหมวดไม่สำเร็จ: ${errText(e)}`, true); }
    await reload();
  };

  /* ---------- search, more ---------- */
  search.oninput = () => { shown = PAGE_TILES; paint(); };
  more.onclick = () => { shown += PAGE_TILES; paint(); };

  whenOpened('shElements', () => { if (!loading || loadError) void reload(); else paint(); });
  return { reload };
}
